import React, { Suspense, useContext, useState } from 'react';
import { MemoryRouter } from 'react-router';
import { useMeasure, useMount } from 'react-use';
import { observer, useLocalStore } from 'mobx-react';
import {
  MEDIAQUERY_DEFAULTS,
  OverlayContainer,
  seasonsStoreContext,
  ServiceSearchDetailParams,
  ServiceSearchParams,
  Theme,
  ThemeProvider,
  useApi,
  useAppService,
  useConfig,
  useTheme
} from '@ibe/components';
import { LoggerFactory } from '@ibe/services';
import { boot, initLanguage } from '../Config/config';
import initI18next from '../Translations/setup';
import { getTheme } from '../Util/themeUtils';
import ExternalParams from '../Config/ExternalParamsContext';
import i18next from 'i18next';
import { MegServiceSearch } from '../Components/SearchWidget/MegServiceSearch';
import { ApiSeason } from '@ibe/api';
import { SearchWidgetParams } from '../Pages/Params/ServiceParamsTransformer';
import useCms from '../Hooks/useCms';
import { ExternalSearchWidgetParams, externalSearchWidgetSessionKey } from './IBESearchWidget';
import DealFinder from '../Components/DealFinder/DealFinder';
import { WidgetProps } from './AppWidget';
import CmsPackage from '../Models/CMS/CmsPackage';
import useCmsPackage from '../Hooks/useCmsPackage';

const logger = LoggerFactory.get('DealFinderWidget');

export type DealFinderWidgetProps = WidgetProps & {
  assignedHotelCodes: string[];
  preselectedHotelCodes: string[];
  hideHotelSelection?: boolean;
  expandDealFinder?: boolean;
  expandDateSelection?: boolean;
  expandPaxSelection?: boolean;
  expandHotelSelection?: boolean;
  alternativePackageCode?: string;
  alternativePackageSalesChannelCode?: string;
  lowPriceThreshold?: number;
  mediumPriceThreshold?: number;
  displayOffers?: boolean;
};

const DealFinderWidget = observer((props: DealFinderWidgetProps) => {
  const {
    packageCode,
    hotelCode,
    salesChannel,
    workflow,
    paxDefinition,
    redirectUrl,
    searchParams: ibeSearchParams,
    position,
    buyTicketsUrl,
    assignedHotelCodes,
    preselectedHotelCodes,
    hideHotelSelection,
    expandDealFinder,
    expandDateSelection,
    expandHotelSelection,
    expandPaxSelection,
    alternativePackageCode,
    alternativePackageSalesChannelCode,
    lowPriceThreshold,
    mediumPriceThreshold,
    displayOffers
  } = props;

  const appService = useAppService();
  const config = useConfig();
  const [bootstrappingFinished, setBootstrappingFinished] = useState<boolean>(false);
  const [seasons, setSeasons] = useState<ApiSeason[]>([]);
  const [packageModel, setPackageModel] = useState<CmsPackage | undefined>();
  const [theme, setTheme] = useState<{} | Theme>(useTheme());
  const [
    workflowSearchWidgetParams,
    setWorkflowSearchWidgetParams
  ] = useState<SearchWidgetParams | null>();

  const api = useApi();
  const seasonsContext = useContext(seasonsStoreContext);
  const seasonsStore = useLocalStore(() => seasonsContext);
  seasonsStore.setApi(api);

  const [componentRef, componentBounds] = useMeasure<HTMLDivElement>();

  const isMobileView = componentBounds.width < Number.parseInt(MEDIAQUERY_DEFAULTS.md);

  const searchParams: Partial<ServiceSearchParams> =
    !!ibeSearchParams && Object.keys(ibeSearchParams).length > 0
      ? {
          code: packageCode,
          wrk: salesChannel,
          salesChannelCode: workflow,
          ...ibeSearchParams
        }
      : {
          code: packageCode,
          wrk: salesChannel,
          salesChannelCode: workflow
        };

  const cmsService = useCms();
  const cmsPackageService = useCmsPackage();

  const saveSearchParamsToSession = (params: Partial<ServiceSearchDetailParams>): void => {
    const ibeSearchWidgetParams: ExternalSearchWidgetParams = {
      serviceSearchParams: {
        ...params,
        code: packageCode,
        hotelCode,
        salesChannelCode: salesChannel,
        wrk: workflow
      },
      searchWidgetParams: workflowSearchWidgetParams ?? {}
    };
    externalSearchWidgetSessionKey.set(ibeSearchWidgetParams);
  };

  useMount(async () => {
    await boot(true, props);
    await initI18next(props);
    const appTheme = await getTheme();
    setTheme(appTheme);

    const lang = initLanguage(i18next.language, config.defaultLanguage);
    appService.setLang(lang);
    appService.setCurrency(config.defaultCurrency);

    seasonsStore.getSeasons().then((res: ApiSeason[]) => {
      setSeasons(
        res.filter(s => (s.salesChannels ? s.salesChannels.includes(salesChannel || '') : true))
      );
    });
    cmsService.fetchPackageWorkflow(workflow).then(wrk => {
      setWorkflowSearchWidgetParams({
        defaultAdults: paxDefinition?.defaultAdults ?? wrk.numberOfAdults,
        maxAdults: paxDefinition?.maxAdults ?? wrk.maxNumberOfAdults,
        maxChildAge: paxDefinition?.maxChildAge ?? wrk.maxChildAge,
        maxChildren: paxDefinition?.maxChildren ?? wrk.maxNumberOfChildren,
        maxDuration: paxDefinition?.maxDuration ?? wrk.maxTripDuration,
        maxRooms: paxDefinition?.maxRooms ?? wrk.maxNumberOfRooms
      });
    });

    cmsPackageService
      .fetchPackage(packageCode as string) //NOSONAR
      .then(returnedPackageModel => {
        if (returnedPackageModel) {
          setPackageModel(returnedPackageModel as CmsPackage); //NOSONAR
        }
      });

    setBootstrappingFinished(true);
  });

  const onSubmit = (params: ServiceSearchParams): void => {
    saveSearchParamsToSession(params);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  return bootstrappingFinished ? (
    <ThemeProvider theme={theme}>
      <div id="iso" ref={componentRef}>
        <Suspense fallback={<></>}>
          <MemoryRouter>
            <ExternalParams {...props}>
              <OverlayContainer>
                {workflowSearchWidgetParams &&
                  (isMobileView ? (
                    <DealFinder
                      packageModel={packageModel}
                      isInWidgetMode
                      isEnabled
                      isMobileView={isMobileView}
                      searchParams={searchParams}
                      onSubmit={onSubmit}
                      datePickerProps={{
                        minDateRangeSpan:
                          workflowSearchWidgetParams?.maxDuration === 0 ? 0 : undefined,
                        maxDateRangeSpan: workflowSearchWidgetParams.maxDuration ?? undefined,
                        seasons: seasons,
                        widgetView: true
                      }}
                      dealFinderWidgetModeProps={{
                        preselectedHotelCodes,
                        assignedHotelCodes,
                        hideHotelSelection: hideHotelSelection,
                        expandDealFinder,
                        expandDateSelection,
                        expandHotelSelection,
                        expandPaxSelection,
                        alternativePackageCode,
                        alternativePackageSalesChannelCode,
                        lowPriceThreshold,
                        mediumPriceThreshold,
                        displayOffers
                      }}
                    />
                  ) : (
                    <MegServiceSearch
                      packageModel={packageModel}
                      searchParams={searchParams}
                      onSubmit={onSubmit}
                      seasons={seasons}
                      maxChildren={workflowSearchWidgetParams.maxChildren}
                      maxChildAge={workflowSearchWidgetParams.maxChildAge}
                      maxAdults={workflowSearchWidgetParams.maxAdults}
                      withRoomCountSelector
                      maxDateRangeSpan={
                        workflowSearchWidgetParams.maxDuration === null
                          ? undefined
                          : workflowSearchWidgetParams.maxDuration
                      }
                      maxRooms={workflowSearchWidgetParams.maxRooms}
                      widgetView
                      withDealFinderWidget
                      position={position}
                      minDateRangeSpan={
                        workflowSearchWidgetParams?.maxDuration === 0 ? 0 : undefined
                      }
                      buyTicketsUrl={buyTicketsUrl}
                      dealFinderWidgetModeProps={{
                        preselectedHotelCodes,
                        assignedHotelCodes,
                        hideHotelSelection: hideHotelSelection,
                        expandDealFinder,
                        expandDateSelection,
                        expandHotelSelection,
                        expandPaxSelection,
                        alternativePackageCode,
                        alternativePackageSalesChannelCode,
                        lowPriceThreshold,
                        mediumPriceThreshold,
                        displayOffers
                      }}
                    />
                  ))}
              </OverlayContainer>
            </ExternalParams>
          </MemoryRouter>
        </Suspense>
      </div>
    </ThemeProvider>
  ) : (
    <div id="iso">
      <></>
    </div>
  );
});

export default DealFinderWidget;
